// Marketplace ratings & reviews — the client-facing copy.
//
// Three families in one fragment, because they are one feature seen from three places:
//   · `rating.*`  the badge, the stars and the Fiverr-style breakdown (card + profile);
//   · `profile.*` the practitioner page at /marketplace/:slug;
//   · `rev.form.*` the review form at /avis, which runs on the PRACTICE's origin but reads
//     this namespace — the copy is the platform's, not the practice's.
//
// `rev.error.<CODE>` covers `REVIEW_ERROR_CODES` member for member, plus `UNKNOWN` for a
// refusal this build predates. The wire's own `detail.message` is English and is never
// rendered (`reviews/errorText.js`); these sentences are what a client actually reads.
export default {
  fr: {
    // ── rating ──────────────────────────────────────────────────────────────
    "rating.summaryTitle": "Avis des clients",
    "rating.reviewCount.one": "1 avis",
    "rating.reviewCount.many": "{count} avis",
    "rating.noReviews": "Pas encore d'avis",
    "rating.ariaStars": "{avg} sur 5 étoiles",
    "rating.histogramRow": "{stars} étoiles : {count} avis sur {total}",
    "rating.sub.communication": "Communication",
    "rating.sub.expertise": "Expertise",
    "rating.sub.value": "Rapport qualité-prix",
    "rating.new": "Nouveau sur la plateforme",
    "rating.badgeAria": "{avg} sur 5 étoiles, {count} avis",

    // ── profil praticien ────────────────────────────────────────────────────
    "profile.backToMarketplace": "Retour au marketplace",
    "profile.visitSite": "Visiter le site",
    "profile.bookSession": "Réserver une séance",
    "profile.continueOn": "Vous continuerez sur {host}",
    "about.title": "Découvrez {name}",
    "about.contact": "Me contacter",
    "about.from": "Pays",
    "about.memberSince": "Membre depuis",
    "about.lastSession": "Dernière séance",
    "about.languages": "Langues",
    "profile.notFoundTitle": "Praticien introuvable",
    "profile.notFoundBody": "Cette adresse ne correspond à aucun cabinet actif. Il a peut-être changé d'adresse ou quitté la plateforme.",
    "profile.servicesTitle": "Prestations",
    "profile.servicesEmpty": "Ce cabinet ne propose aucune prestation réservable pour le moment.",
    "profile.duration": "{minutes} min",
    "profile.reviewsTitle": "Avis",
    "profile.reviewsEmpty": "Ce cabinet n'a pas encore reçu d'avis.",
    "profile.replyLabel": "Réponse du praticien",
    "profile.page.info": "{from}–{to} sur {total}",
    "profile.page.prev": "Précédent",
    "profile.page.next": "Suivant",

    // ── formulaire d'avis ───────────────────────────────────────────────────
    "rev.form.missingTokenTitle": "Lien incomplet",
    "rev.form.missingTokenBody": "Ce lien ne contient pas le code qui permet de laisser un avis. Ouvrez-le directement depuis le courriel que vous avez reçu.",
    "rev.form.refusalTitle": "Ce lien n'est pas valide",
    "rev.form.expiredTitle": "Ce lien a expiré",
    "rev.form.expiredBody": "La période pour laisser un avis sur ce rendez-vous est terminée. Merci quand même d'avoir pris le temps.",
    "rev.form.disabledTitle": "Les avis sont en pause",
    "rev.form.disabledBody": "La collecte d'avis est temporairement suspendue sur la plateforme. Réessayez plus tard.",
    "rev.form.submittedTitle": "Merci pour votre avis",
    "rev.form.submittedBody": "Votre avis est publié sur le profil du cabinet. Il ne peut plus être modifié.",
    "rev.form.overallLegend": "Votre note globale",
    "rev.form.starLabel": "{n} sur 5",
    "rev.form.subHint": "Facultatif : détaillez votre expérience.",
    "rev.form.sub.communication": "Communication",
    "rev.form.sub.expertise": "Expertise",
    "rev.form.sub.value": "Rapport qualité-prix",
    "rev.form.textLabel": "Votre commentaire (facultatif)",
    "rev.form.textPlaceholder": "Ce qui vous a marqué pendant cette séance…",
    "rev.form.textCounter": "{n}/{max}",
    "rev.form.submit": "Publier mon avis",
    "rev.form.submitting": "Publication…",
    "rev.form.thanks": "Votre avis a été publié.",
    "rev.form.marketplaceLink": "Voir le profil du cabinet",

    // ── refus ───────────────────────────────────────────────────────────────
    "rev.error.REVIEWS_DISABLED": "La collecte d'avis est suspendue sur la plateforme : votre avis ne peut pas être enregistré pour l'instant.",
    "rev.error.REVIEW_TOKEN_UNKNOWN": "Ce lien d'avis n'est pas valide. Vérifiez que vous l'avez ouvert en entier depuis votre courriel.",
    "rev.error.REVIEW_TOKEN_EXPIRED": "Ce lien d'avis a expiré.",
    "rev.error.REVIEW_ALREADY_SUBMITTED": "Un avis a déjà été déposé pour ce rendez-vous.",
    "rev.error.REVIEW_INVALID": "Cet avis n'est pas valide : vérifiez la note et la longueur du commentaire.",
    "rev.error.REVIEW_NOT_FOUND": "Cet avis est introuvable.",
    "rev.error.REVIEW_ALREADY_REPLIED": "Une réponse a déjà été publiée pour cet avis.",
    "rev.error.REVIEW_ALREADY_FLAGGED": "Cet avis a déjà été signalé.",
    "rev.error.REVIEW_ALREADY_HIDDEN": "Cet avis est déjà masqué.",
    "rev.error.REVIEW_NOT_HIDDEN": "Cet avis n'est pas masqué.",
    "rev.error.REVIEW_PROGRAM_INVALID": "Ces réglages du programme d'avis ne sont pas valides.",
    "rev.error.PRACTITIONER_NOT_FOUND": "Ce cabinet est introuvable sur le marketplace.",
    "rev.error.RATE_LIMITED": "Trop de tentatives : réessayez dans un instant.",
    "rev.error.UNKNOWN": "Une erreur est survenue et nous ne pouvons pas en dire plus.",
  },
  en: {
    "rating.summaryTitle": "Client reviews",
    "rating.reviewCount.one": "1 review",
    "rating.reviewCount.many": "{count} reviews",
    "rating.noReviews": "No reviews yet",
    "rating.ariaStars": "{avg} out of 5 stars",
    "rating.histogramRow": "{stars} stars: {count} of {total} reviews",
    "rating.sub.communication": "Communication",
    "rating.sub.expertise": "Expertise",
    "rating.sub.value": "Value for money",
    "rating.new": "New on the platform",
    "rating.badgeAria": "{avg} out of 5 stars, {count} reviews",

    "profile.backToMarketplace": "Back to the marketplace",
    "profile.visitSite": "Visit website",
    "profile.bookSession": "Book a session",
    "profile.continueOn": "You will continue on {host}",
    "about.title": "Get to know {name}",
    "about.contact": "Contact me",
    "about.from": "From",
    "about.memberSince": "Member since",
    "about.lastSession": "Last session",
    "about.languages": "Languages",
    "profile.notFoundTitle": "Practitioner not found",
    "profile.notFoundBody": "This address does not match any active practice. They may have moved or left the platform.",
    "profile.servicesTitle": "Services",
    "profile.servicesEmpty": "This practice has no bookable service at the moment.",
    "profile.duration": "{minutes} min",
    "profile.reviewsTitle": "Reviews",
    "profile.reviewsEmpty": "This practice has not received a review yet.",
    "profile.replyLabel": "Practitioner's reply",
    "profile.page.info": "{from}–{to} of {total}",
    "profile.page.prev": "Previous",
    "profile.page.next": "Next",

    "rev.form.missingTokenTitle": "Incomplete link",
    "rev.form.missingTokenBody": "This link does not carry the code that lets you leave a review. Open it straight from the email you received.",
    "rev.form.refusalTitle": "This link is not valid",
    "rev.form.expiredTitle": "This link has expired",
    "rev.form.expiredBody": "The window to review this appointment is over. Thank you for taking the time anyway.",
    "rev.form.disabledTitle": "Reviews are paused",
    "rev.form.disabledBody": "Review collection is temporarily paused on the platform. Please try again later.",
    "rev.form.submittedTitle": "Thank you for your review",
    "rev.form.submittedBody": "Your review is published on the practice's profile. It can no longer be edited.",
    "rev.form.overallLegend": "Your overall rating",
    "rev.form.starLabel": "{n} out of 5",
    "rev.form.subHint": "Optional: tell us more about your experience.",
    "rev.form.sub.communication": "Communication",
    "rev.form.sub.expertise": "Expertise",
    "rev.form.sub.value": "Value for money",
    "rev.form.textLabel": "Your comment (optional)",
    "rev.form.textPlaceholder": "What stayed with you from this session…",
    "rev.form.textCounter": "{n}/{max}",
    "rev.form.submit": "Publish my review",
    "rev.form.submitting": "Publishing…",
    "rev.form.thanks": "Your review has been published.",
    "rev.form.marketplaceLink": "See the practice's profile",

    "rev.error.REVIEWS_DISABLED": "Review collection is paused on the platform: your review cannot be saved right now.",
    "rev.error.REVIEW_TOKEN_UNKNOWN": "This review link is not valid. Check that you opened it in full from your email.",
    "rev.error.REVIEW_TOKEN_EXPIRED": "This review link has expired.",
    "rev.error.REVIEW_ALREADY_SUBMITTED": "A review has already been left for this appointment.",
    "rev.error.REVIEW_INVALID": "This review is not valid: check the rating and the length of the comment.",
    "rev.error.REVIEW_NOT_FOUND": "This review no longer exists.",
    "rev.error.REVIEW_ALREADY_REPLIED": "A reply has already been published for this review.",
    "rev.error.REVIEW_ALREADY_FLAGGED": "This review has already been flagged.",
    "rev.error.REVIEW_ALREADY_HIDDEN": "This review is already hidden.",
    "rev.error.REVIEW_NOT_HIDDEN": "This review is not hidden.",
    "rev.error.REVIEW_PROGRAM_INVALID": "These review-program settings are not valid.",
    "rev.error.PRACTITIONER_NOT_FOUND": "This practice cannot be found on the marketplace.",
    "rev.error.RATE_LIMITED": "Too many attempts — try again in a moment.",
    "rev.error.UNKNOWN": "Something went wrong and we cannot say what.",
  },
  es: {
    "rating.summaryTitle": "Opiniones de clientes",
    "rating.reviewCount.one": "1 opinión",
    "rating.reviewCount.many": "{count} opiniones",
    "rating.noReviews": "Aún no hay opiniones",
    "rating.ariaStars": "{avg} de 5 estrellas",
    "rating.histogramRow": "{stars} estrellas: {count} de {total} opiniones",
    "rating.sub.communication": "Comunicación",
    "rating.sub.expertise": "Profesionalidad",
    "rating.sub.value": "Relación calidad-precio",
    "rating.new": "Nuevo en la plataforma",
    "rating.badgeAria": "{avg} de 5 estrellas, {count} opiniones",

    "profile.backToMarketplace": "Volver al marketplace",
    "profile.visitSite": "Visitar el sitio web",
    "profile.bookSession": "Reservar una sesión",
    "profile.continueOn": "Continuarás en {host}",
    "about.title": "Conoce a {name}",
    "about.contact": "Contactar",
    "about.from": "País",
    "about.memberSince": "Miembro desde",
    "about.lastSession": "Última sesión",
    "about.languages": "Idiomas",
    "profile.notFoundTitle": "Profesional no encontrado",
    "profile.notFoundBody": "Esta dirección no corresponde a ningún consultorio activo. Puede que haya cambiado de dirección o dejado la plataforma.",
    "profile.servicesTitle": "Servicios",
    "profile.servicesEmpty": "Este consultorio no ofrece ningún servicio reservable por el momento.",
    "profile.duration": "{minutes} min",
    "profile.reviewsTitle": "Opiniones",
    "profile.reviewsEmpty": "Este consultorio todavía no ha recibido opiniones.",
    "profile.replyLabel": "Respuesta del profesional",
    "profile.page.info": "{from}–{to} de {total}",
    "profile.page.prev": "Anterior",
    "profile.page.next": "Siguiente",

    "rev.form.missingTokenTitle": "Enlace incompleto",
    "rev.form.missingTokenBody": "Este enlace no contiene el código que permite dejar una opinión. Ábralo directamente desde el correo que recibió.",
    "rev.form.refusalTitle": "Este enlace no es válido",
    "rev.form.expiredTitle": "Este enlace ha caducado",
    "rev.form.expiredBody": "El plazo para opinar sobre esta cita ha terminado. Gracias igualmente por su tiempo.",
    "rev.form.disabledTitle": "Las opiniones están en pausa",
    "rev.form.disabledBody": "La recogida de opiniones está suspendida temporalmente en la plataforma. Inténtelo más tarde.",
    "rev.form.submittedTitle": "Gracias por su opinión",
    "rev.form.submittedBody": "Su opinión está publicada en el perfil del consultorio. Ya no se puede modificar.",
    "rev.form.overallLegend": "Su valoración global",
    "rev.form.starLabel": "{n} de 5",
    "rev.form.subHint": "Opcional: cuéntenos más sobre su experiencia.",
    "rev.form.sub.communication": "Comunicación",
    "rev.form.sub.expertise": "Profesionalidad",
    "rev.form.sub.value": "Relación calidad-precio",
    "rev.form.textLabel": "Su comentario (opcional)",
    "rev.form.textPlaceholder": "Lo que más le marcó de esta sesión…",
    "rev.form.textCounter": "{n}/{max}",
    "rev.form.submit": "Publicar mi opinión",
    "rev.form.submitting": "Publicando…",
    "rev.form.thanks": "Su opinión se ha publicado.",
    "rev.form.marketplaceLink": "Ver el perfil del consultorio",

    "rev.error.REVIEWS_DISABLED": "La recogida de opiniones está en pausa en la plataforma: su opinión no puede guardarse ahora mismo.",
    "rev.error.REVIEW_TOKEN_UNKNOWN": "Este enlace de opinión no es válido. Compruebe que lo abrió completo desde su correo.",
    "rev.error.REVIEW_TOKEN_EXPIRED": "Este enlace de opinión ha caducado.",
    "rev.error.REVIEW_ALREADY_SUBMITTED": "Ya se ha dejado una opinión para esta cita.",
    "rev.error.REVIEW_INVALID": "Esta opinión no es válida: revise la valoración y la longitud del comentario.",
    "rev.error.REVIEW_NOT_FOUND": "Esta opinión ya no existe.",
    "rev.error.REVIEW_ALREADY_REPLIED": "Ya se ha publicado una respuesta a esta opinión.",
    "rev.error.REVIEW_ALREADY_FLAGGED": "Esta opinión ya ha sido señalada.",
    "rev.error.REVIEW_ALREADY_HIDDEN": "Esta opinión ya está oculta.",
    "rev.error.REVIEW_NOT_HIDDEN": "Esta opinión no está oculta.",
    "rev.error.REVIEW_PROGRAM_INVALID": "Estos ajustes del programa de opiniones no son válidos.",
    "rev.error.PRACTITIONER_NOT_FOUND": "No se encuentra este consultorio en el marketplace.",
    "rev.error.RATE_LIMITED": "Demasiados intentos: vuelva a probar en un momento.",
    "rev.error.UNKNOWN": "Ha ocurrido un error y no podemos decir más.",
  },
};
